import {computed, isReactive, type ComputedRef} from 'vue';

import type {LinkedLabelLink} from '@/services/GraphicService/LabelGraphic/linkedLabel';

export interface NestableFeature {
  id: string;
  linkedLabel?: LinkedLabelLink | null;
}

/** Null for orphans whose source is gone, so they keep a card of their own. */
export function linkedLabelSource<F extends NestableFeature>(
  feature: F | null | undefined,
  features: Map<string, F>,
): F | null {
  const sourceId = feature?.linkedLabel?.featureId;
  if (!sourceId || sourceId === feature.id) return null;
  return features.get(sourceId) ?? null;
}

export function groupLinkedLabels<F extends NestableFeature>(features: Map<string, F>): Map<string, F[]> {
  const groups = new Map<string, F[]>();
  for (const feature of features.values()) {
    const source = linkedLabelSource(feature, features);
    if (!source) continue;
    const labels = groups.get(source.id) ?? [];
    labels.push(feature);
    groups.set(source.id, labels);
  }
  return groups;
}

/** One grouping per feature manager, so each card's lookup doesn't rescan every feature. */
const groupingsByFeatures = new WeakMap<Map<string, NestableFeature>, ComputedRef<Map<string, NestableFeature[]>>>();

export function linkedLabelsOf<F extends NestableFeature>(features: Map<string, F>, sourceId: string): F[] {
  // A computed over a plain Map would never invalidate
  if (!isReactive(features)) return groupLinkedLabels(features).get(sourceId) ?? [];

  let grouping = groupingsByFeatures.get(features);
  if (!grouping) {
    grouping = computed(() => groupLinkedLabels<NestableFeature>(features));
    groupingsByFeatures.set(features, grouping);
  }
  return (grouping.value.get(sourceId) ?? []) as F[];
}
